import { FC, useCallback, useMemo, useState } from 'react';
import HelpIcon from '@mui/icons-material/Help';

import cn from 'utils/cn';
import MenuBar from '@3.0/MenuBar/MenuBar';
import { ActionButton } from '@3.0/ActionButtons';
import { KeyboardArrowUp } from '@mui/icons-material';

type Props = {
  helpContent: React.ReactNode | null;
};

const HelpButton: FC<Props> = ({ helpContent }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const button = event.currentTarget;
    setAnchorEl((prevAnchorEl) => (prevAnchorEl ? null : button));
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const open = useMemo(() => Boolean(anchorEl), [anchorEl]);
  const id = useMemo(() => (open ? 'modal-help-popover' : undefined), [open]);

  return (
    <div>
      <button
        aria-describedby={id}
        aria-label="Toggle help content"
        data-testid="toggle-show-help"
        className={cn(
          'tw:box-border tw:text-secondary-400 tw:cursor-pointer tw:border-0 tw:border-l tw:border-primary-600 h-inherit tw:px-3 tw:py-4 tw:w-12 tw:relative tw:flex tw:items-center tw:justify-center',
          open
            ? 'tw:bg-accent-100 tw:text-primary-100'
            : 'tw:bg-transparencies-primary-50025 tw:hover:bg-transparencies-primary-5005'
        )}
        onClick={handleClick}
      >
        <HelpIcon
          style={{
            fontSize: 24,
          }}
        />
      </button>

      {open && (
        <div
          className="tw:absolute tw:top-14 tw:right-0 tw:w-[320px] tw:bg-primary-100 tw:rounded tw:border-l tw:border-r tw:border-b tw:border-transparencies-secondary-60025"
          data-testid="help-popover-content"
          style={{ zIndex: 1501, boxShadow: '0 0 60px 8px rgba(0, 0, 0, 0.25)' }}
          id={id}
        >
          <MenuBar
            label="Help"
            actionButtons={[
              <ActionButton type="button" testId="hide-help" onClick={handleClose}>
                <KeyboardArrowUp />
                <span>Hide Help</span>
              </ActionButton>,
            ]}
          />
          {helpContent}
        </div>
      )}
    </div>
  );
};

export default HelpButton;
